import React from "react";

import { LOW_BATTERY } from "../utils/deviceStatus";

/**
 * A single creative rendered the way the Way2News app shows it.
 *
 * Two shapes, matching what a reader sees on their phone:
 *  - full image / gallery: the creative centred on a black screen, with the
 *    "Ad" badge in the top right.
 *  - promoted story: a feed card - banner, headline, body, call to action,
 *    the SPONSORED AD label, and the post action bar beneath it.
 *
 * Icons are inline SVG rather than <sl-icon>, which pulls its sprites from
 * jsdelivr: html-to-image has to inline every resource, and a cross origin
 * icon would come out blank. Styles live in assets/styles/screenshotFrame.scss.
 */

const InfoIcon = ({ size = 13 }) => (
  <svg
    className="ss-icon"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
  </svg>
);

const ThumbsUpIcon = () => (
  <svg
    className="ss-icon"
    width="20.58"
    height="20.58"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 10v12" />
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
  </svg>
);

const BookmarkIcon = () => (
  <svg
    className="ss-icon"
    width="19.5"
    height="19.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="ss-icon" width="19.5" height="19.5" viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
  </svg>
);

const MoreIcon = () => (
  <svg className="ss-icon" width="19.5" height="19.5" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="12" cy="19" r="1.6" />
  </svg>
);

const SendIcon = () => (
  <svg className="ss-icon" width="22.75" height="22.75" viewBox="0 0 24 24" fill="#2f8bff">
    <path d="M14 9V5l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z" />
  </svg>
);

/** The post action bar the app draws under a promoted story. */
const ActionBar = () => (
  <div className="ss-actionbar">
    <span className="ss-actionbar__item">
      <ThumbsUpIcon />
      <span>0</span>
    </span>
    <span className="ss-actionbar__item">
      <BookmarkIcon />
      <span>Bookmark</span>
    </span>
    <span className="ss-actionbar__item">
      <WhatsAppIcon />
      <span>SHARE</span>
    </span>
    <span className="ss-actionbar__item">
      <InfoIcon size={19.5} />
    </span>
    <span className="ss-actionbar__item">
      <MoreIcon />
    </span>
    <span className="ss-actionbar__item">
      <SendIcon />
    </span>
  </div>
);

const AlarmIcon = () => (
  <svg
    className="ss-icon"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2 2M5 3 2 6m20 0-3-3" />
  </svg>
);

/** Pixel signal: a right triangle, the lit part a smaller copy of it. */
const TriangleSignal = ({ bars }) => {
  const f = Math.min(Math.max(bars, 0), 4) / 4;
  const x = 2 + 20 * f;
  return (
    <svg className="ss-icon" width="11.38" height="11.38" viewBox="2 2 20 20" fill="currentColor" style={{ marginRight: -2.17 }}>
      <path d="M22 2v20H2z" opacity="0.32" />
      <path d={`M2 22L${x} 22L${x} ${22 - 20 * f}Z`} />
    </svg>
  );
};

/** Cell signal as four bars, the first `bars` of them lit. */
const BarsSignal = ({ bars }) => (
  <svg className="ss-icon" width="14.08" height="14.08" viewBox="0 0 24 24" fill="currentColor">
    {[0, 1, 2, 3].map((i) => (
      <rect
        key={i}
        x={2 + i * 5.5}
        y={20 - (i + 1) * 4.5}
        width="4"
        height={(i + 1) * 4.5}
        rx="0.8"
        opacity={i < bars ? 1 : 0.32}
      />
    ))}
  </svg>
);

const SignalIcon = ({ bars, variant }) =>
  variant === "triangle" ? <TriangleSignal bars={bars} /> : <BarsSignal bars={bars} />;

/** The VoLTE arcs drawn after "Vo". */
const VolteArcs = () => (
  <svg className="ss-icon" width="4.33" height="4.33" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.6" strokeLinecap="round">
    <path d="M4 10a10 10 0 0 1 10 10M4 3a17 17 0 0 1 17 17" />
  </svg>
);

const WifiIcon = () => (
  <svg
    className="ss-icon"
    width="15.17"
    height="15.17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
  >
    <path d="M2 9a15 15 0 0 1 20 0M5.5 12.8a10 10 0 0 1 13 0M9 16.4a5 5 0 0 1 6 0" />
    <circle cx="12" cy="19.6" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

const BOLT = "M7.2 1 2.6 7.6h3L4.8 11 9.4 4.4h-3z";

// Low battery is red in every skin; otherwise the glyph takes the bar's own
// white. Charging shows as the bolt alone, as on a Pixel.
const batteryColor = ({ level }) =>
  level <= LOW_BATTERY ? "#ff3b30" : "currentColor";

/** Battery glyph, vertical (Pixel) or horizontal (Samsung and most skins). */
const BatteryIcon = ({ battery, orientation }) => {
  const level = Math.min(Math.max(battery.level, 0), 100);
  const color = batteryColor(battery);

  if (orientation === "horizontal") {
    const fill = (14 * level) / 100;
    return (
      <svg className="ss-icon" width="19.5" height="11.92" viewBox="0 0 21 12" fill="none" style={{ color }}>
        <rect x="1" y="1" width="16" height="10" rx="2.4" stroke="currentColor" strokeWidth="1.3" />
        <path d="M19 4.4v3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="2.5" y="2.5" width={fill} height="7" rx="1.2" fill="currentColor" />
        {battery.charging && (
          <path d={BOLT} fill="#fff" transform="translate(4.4 0.2)" stroke="#000" strokeWidth="0.4" />
        )}
      </svg>
    );
  }

  // Body 8x15 inside a 10x17 box; the fill rises from the bottom. The bolt is
  // dark where the fill is under it and light where it is not.
  const fill = (13.6 * level) / 100;
  const boltOnFill = 16 - fill < 8.5;
  return (
    <svg className="ss-icon" width="7.58" height="13" viewBox="0 0 10 17" fill="none" style={{ color }}>
      <rect x="3.4" y="0.2" width="3.2" height="1.4" rx="0.6" fill="currentColor" />
      <rect x="0.8" y="1.6" width="8.4" height="15" rx="1.8" stroke="currentColor" strokeWidth="1.2" />
      <rect x="1.4" y={16 - fill} width="7.2" height={fill} rx="0.8" fill="currentColor" />
      {battery.charging && (
        <path d="M5.6 4.4 3.1 9.3h2l-.6 3.8 2.5-4.9h-2z" fill={boltOnFill ? "#000" : "#fff"} />
      )}
    </svg>
  );
};

/** The phone's own status bar, drawn so the frame reads as a real screenshot. */
const StatusBar = ({ status }) => {
  const { time, look, battery, network, bars, speed } = status;
  const triangle = look.signal === "triangle";
  const percent = (
    <span
      className="ss-statusbar__pct"
      style={battery.level <= LOW_BATTERY ? { color: "#ff3b30" } : undefined}
    >
      {battery.level}%
    </span>
  );

  return (
    <div className="ss-statusbar">
      <span className="ss-statusbar__time">{time}</span>
      <span className="ss-statusbar__right">
        {look.speed && <span className="ss-statusbar__net">{speed}</span>}
        {look.alarm && <AlarmIcon />}
        {look.volte && network.kind === "cell" && (
          <span className="ss-statusbar__volte">
            <span className="ss-statusbar__vo">
              Vo
              <VolteArcs />
            </span>
            <span>LTE</span>
          </span>
        )}
        {network.kind === "cell" ? (
          <>
            <span className={triangle ? "ss-statusbar__gen" : "ss-statusbar__net"}>
              {network.label}
            </span>
            <SignalIcon bars={bars} variant={look.signal} />
          </>
        ) : (
          // On Wi-Fi the phone still shows its cell bars, just without the
          // network label.
          <>
            <SignalIcon bars={bars} variant={look.signal} />
            <WifiIcon />
          </>
        )}
        {look.percent === "before" && percent}
        <BatteryIcon battery={battery} orientation={look.battery} />
        {look.percent === "after" && percent}
      </span>
    </div>
  );
};

/** Gesture navigation bar at the foot of the screen. */
const NavBar = () => (
  <div className="ss-navbar">
    <span className="ss-navbar__pill" />
  </div>
);

const CallToAction = ({ button }) =>
  button ? (
    <div className="ss-cta">
      <button
        type="button"
        style={{ backgroundColor: button.background, color: button.color }}
      >
        {button.text}
      </button>
    </div>
  ) : null;

/** Play button drawn over a video thumbnail. */
const PlayButton = () => (
  <span className="ss-play">
    <svg width="28.17" height="28.17" viewBox="0 0 24 24" fill="#fff">
      <path d="M8 5v14l11-7z" />
    </svg>
  </span>
);

/**
 * The creative image. data-src carries the original URL so the downloader can
 * swap in the inlined copy; an empty URL renders nothing and leaves the black
 * (or white) surface behind it.
 */
const CreativeImg = ({ url, alt }) =>
  url ? <img className="ss-creative-img" src={url} data-src={url} alt={alt} /> : null;

const AdBadge = () => (
  // "Ad" then the info mark, the order the app shows. Note the widget asset at
  // payments/ad-label-icon.svg has them the other way round; its 30% black
  // wash and white marks are reused here.
  <span className="ss-ad-badge">
    Ad
    <InfoIcon size={10.83} />
  </span>
);

function ScreenshotFrame({ frame, language, status }) {
  if (!frame) return null;

  const { kind, imgUrl, videoThumb, bandAlign, showPlay, headline, bodySegments, button } = frame;

  // Full image, gallery and full video ads take over the whole screen, so the
  // frame is a whole phone: status bar, the creative centred on black and
  // letterboxed to whatever its own aspect ratio needs, then the gesture bar.
  if (kind === "screen") {
    return (
      <div className="ss-frame ss-frame--screen">
        <StatusBar status={status} />
        <div className="ss-screen-body">
          <CreativeImg url={imgUrl} alt="Creative" />
          {showPlay && <PlayButton />}
          <AdBadge />
        </div>
        <NavBar />
      </div>
    );
  }

  // Half video: the background image fills the screen and the video band sits
  // at the top, middle or bottom of it.
  if (kind === "half") {
    return (
      <div className="ss-frame ss-frame--screen">
        <StatusBar status={status} />
        <div className="ss-screen-body ss-screen-body--half" style={{ alignItems: bandAlign === "start" ? "flex-start" : bandAlign === "end" ? "flex-end" : "center" }}>
          <CreativeImg url={imgUrl} alt="Creative background" />
          <div className="ss-video-band">
            <CreativeImg url={videoThumb} alt="Video thumbnail" />
            <PlayButton />
          </div>
          <AdBadge />
        </div>
        <NavBar />
      </div>
    );
  }

  // Promoted story: a news-style card on a whole phone screen. The banner is
  // capped at 40% of the screen, the copy follows it, and the sponsored label
  // and action bar sit at the foot, above the gesture bar.
  return (
    <div className="ss-frame ss-frame--screen ss-frame--card">
      <StatusBar status={status} />
      <div className="ss-card-body">
        <div className="ss-card-main">
          <div className={`ss-banner${imgUrl ? "" : " ss-banner--empty"}`}>
            <CreativeImg url={imgUrl} alt="Creative banner" />
            {showPlay && <PlayButton />}
          </div>

          <div className="ss-content">
            {headline && (
              <h4 className={`ss-heading-${language}`}>{headline}</h4>
            )}
            {bodySegments?.length > 0 && (
              <p className={`ss-body-${language}`}>
                {bodySegments.map((segment, index) => (
                  <span key={index} className={segment.link ? "ss-body-link" : undefined}>
                    {segment.text}
                  </span>
                ))}
              </p>
            )}
          </div>

          <CallToAction button={button} />

          <div className="ss-sponsored">SPONSORED AD</div>
        </div>
        <ActionBar />
      </div>
      <NavBar />
    </div>
  );
}

export default ScreenshotFrame;
